import { useCurrentPeriodFilter } from '@va/dashboard/util-hooks';
import { post } from '@va/http-client';
import { PrivacyLevelsEnum } from '@va/standalone/shared/types';
import { DeviceTypesEnum } from '@va/types/device';
import { Where } from '@va/types/filters';
import { DateRange, PaginatedResponse, Sorting, SortingDirection } from '@va/types/requests';
import { Pagination } from '@va/types/table';
import { TimePeriod } from '@va/types/time';
import { useFetchData, useFetchDataParams, useLazyDataFetch } from '@va/util/hooks';
import { RequestCanceler, useCancelOnUnmount } from '@va/util/misc';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';

export type VisitDimension =
  | 'id'
  | 'sessionKey'
  | 'url'
  | 'pageKey'
  | 'visitorKey'
  | 'referrerUrl'
  | 'browserName'
  | 'browserVersion'
  | 'cityCode'
  | 'cityName'
  | 'countryCode'
  | 'countryName'
  | 'deviceType'
  | 'display'
  | 'ip'
  | 'lat'
  | 'lng'
  | 'platform'
  | 'adCampaignKey'
  | 'adCampaignSource'
  | 'adCampaignMedium'
  | 'adCampaignContent'
  | 'adCampaignLabel'
  | 'unixTs'
  | 'title'
  | 'companyOrgName'
  | 'companyOrgType'
  | 'adCampaignTerm'
  | 'adCampaignSourcePlatform'
  | 'adCampaignCreativeFormat'
  | 'adCampaignMarketingTactic'
  | 'tags'
  | 'queryString'
  | 'fragment'
  | 'privacyLevel'
  | 'channel'
  | 'category'
  | 'duration'
  | 'firstVisit'
  | 'userAgent'
  | 'unixTsMs'
  | 'metadata';

type Visit = {
  id: string;
  sessionKey: string;
  url: string;
  pageKey: string;
  visitorKey: string;
  referrerUrl: string;
  browserName: string;
  browserVersion: string;
  cityCode: string;
  cityName: string;
  countryCode: string;
  countryName: string;
  deviceType: DeviceTypesEnum;
  display: string;
  ip: string;
  lat: number;
  lng: number;
  platform: string;
  adCampaignKey: string;
  adCampaignSource: string;
  adCampaignMedium: string;
  adCampaignContent: string;
  adCampaignLabel: string;
  unixTs: number;
  title: string;
  companyOrgName: string | null;
  companyOrgType: string | null;
  adCampaignTerm: string;
  adCampaignSourcePlatform: string;
  adCampaignCreativeFormat: string;
  adCampaignMarketingTactic: string;
  tags: string[];
  queryString: string;
  fragment: string;
  privacyLevel: PrivacyLevelsEnum;
  channel: string;
  category: string;
  duration: number;
  firstVisit: boolean;
  userAgent: string;
  unixTsMs: number;
  metadata: Record<string, unknown>;
};

type VisitPayload<D extends VisitDimension[]> = {
  dimensions?: D;
  dateRange: DateRange;
  order?: Sorting<D[number]>[];
  pagination?: Pagination;
  where?: Where;
};

export type VisitResponse<D extends VisitDimension> = {
  [Key in D]: Visit[Key];
};

type HelperParams<T> = {
  trace?: string;
  config?: useFetchDataParams<T>['config'];
};

export const fetchRawVisits = <D extends VisitDimension[]>(websiteId: string, payload: VisitPayload<D>) => {
  return post<PaginatedResponse<VisitResponse<D[number]>[]>>(`/v2/websites/${websiteId}/raw/visits`, {}, payload);
};

export type VisitMetric =
  | 'visitCount'
  | 'activeVisitCount'
  | 'previousVisitCount'
  | 'visitCountPercentageChange'
  | 'averageActiveViewportDuration'
  | 'overallAverageActiveViewportDuration'
  | 'averageScrollDepth'
  | 'overallAverageScrollDepth'
  | 'pageCount'
  | 'sessionCount'
  | 'bounceRate'
  | 'advancedBounceRate'
  | 'averageSessionDuration'
  | 'averageVisitDuration'
  | 'visitorCount'
  | 'newVisitorCount'
  | 'returningVisitorCount'
  | 'continuationRate'
  | 'sessionConversionRate';

// `date` is a special dimension used for timeseries responses
export type VisitMetricDimension = VisitDimension | 'date';

type VisitDimensionModel = Visit & { date: string };

export type VisitMetricsResponse<M extends VisitMetric, D extends VisitMetricDimension = never> = {
  [Key in D]: Key extends keyof VisitDimensionModel ? VisitDimensionModel[Key] : never;
} & {
  [Key in M]: number;
};

type VisitMetricsPayload<M extends VisitMetric[], D extends VisitMetricDimension[] = never> = {
  dimensions?: D;
  metrics: M;
  dateRange: DateRange;
  granularity?: TimePeriod | '5min';
  where?: Where;
  pagination?: Pagination;
  order?: Sorting<M[number]>[];
};

export const fetchVisitMetrics = <M extends VisitMetric[], D extends VisitMetricDimension[] = never>(
  websiteId: string,
  query: Record<string, string>,
  payload: VisitMetricsPayload<M, D>,
  signal?: AbortSignal,
) => {
  return post<PaginatedResponse<VisitMetricsResponse<M[number], D[number]>[]>>(
    `/v2/websites/${websiteId}/analytics/visits`,
    query,
    payload,
    { signal },
  );
};

type VisitMetricsHelperParams<T> = {
  trace: string;
  controller?: AbortController;
  config?: useFetchDataParams<T>['config'];
};

export const useVisitMetrics = <
  M extends VisitMetric[],
  D extends VisitMetricDimension[] = never,
  T = PaginatedResponse<VisitMetricsResponse<M[number], D[number]>[]>,
>(
  {
    trace,
    controller,
    config,
    dateRange,
    metrics,
    dimensions,
    order,
    pagination,
    where,
    granularity,
  }: VisitMetricsPayload<M, D> & VisitMetricsHelperParams<T>,
  shouldFetch: boolean = true,
  mapper?: (response: PaginatedResponse<VisitMetricsResponse<M[number], D[number]>[]>) => T | Promise<T>,
) => {
  const { websiteId } = useCurrentPeriodFilter();

  const payload = useMemo<VisitMetricsPayload<M, D>>(
    () => ({
      metrics,
      dimensions,
      order,
      pagination,
      where,
      granularity,
      dateRange,
    }),
    [dateRange, dimensions, granularity, metrics, order, pagination, where],
  );

  const requestId = useMemo(() => RequestCanceler.generateId(), []);
  useCancelOnUnmount(requestId);

  const fetchFn = useCallback(
    async (_: string, websiteId: string, query: Record<string, string>, payload: VisitMetricsPayload<M, D>) => {
      RequestCanceler.onRequestStart(requestId);
      const res = await fetchVisitMetrics(websiteId, query, payload, controller?.signal);
      RequestCanceler.onRequestEnd(requestId);
      return res;
    },
    [controller, requestId],
  );

  return useLazyDataFetch(
    shouldFetch ? [`/v2/websites/${websiteId}/analytics/visits`, websiteId, { trace }, payload] : null,
    {
      ...config,
      onError: () => RequestCanceler.onRequestEnd(requestId),
    },
    mapper,
    fetchFn,
  );
};

export const useRawVisits = <D extends VisitDimension[], T = PaginatedResponse<VisitResponse<D[number]>[]>>(
  { trace, config, ...payloadParam }: Omit<VisitPayload<D>, 'dateRange'> & HelperParams<T>,
  shouldFetch: boolean = true,
  mapper?: (response: PaginatedResponse<VisitResponse<D[number]>[]>) => T,
) => {
  const { from, until, websiteId } = useCurrentPeriodFilter();

  const payload = useMemo<VisitPayload<D>>(
    () => ({ ...payloadParam, dateRange: { start: from, end: until } }),
    [from, payloadParam, until],
  );

  const fetchFn = useCallback((url: string, _query: Record<string, string>, payload: VisitPayload<D>) => {
    return post(url, {}, payload);
  }, []);

  const key = useMemo(() => [`/v2/websites/${websiteId}/raw/visits`, { trace }, payload], [payload, trace, websiteId]);

  return useLazyDataFetch(shouldFetch ? key : null, config, mapper, fetchFn);
};

export const useLastVisitDate = (
  shouldFetch: boolean = true,
  config?: useFetchDataParams<Dayjs | undefined>['config'],
) => {
  const { from, until, websiteId } = useCurrentPeriodFilter();

  const payload = useMemo<VisitPayload<['unixTs']>>(
    () => ({
      dimensions: ['unixTs'],
      dateRange: { start: from, end: until },
      order: [{ member: 'unixTs', direction: SortingDirection.Desc }],
      pagination: { page: 1, pageSize: 1 },
    }),
    [from, until],
  );

  const fetchFn = useCallback(
    (_key: string, payload: VisitPayload<['unixTs']>) => {
      return post<PaginatedResponse<VisitResponse<'unixTs'>[]>>(`/v2/websites/${websiteId}/raw/visits`, {}, payload);
    },
    [websiteId],
  );

  const mapper = useCallback((response: PaginatedResponse<VisitResponse<'unixTs'>[]>): Dayjs | undefined => {
    const unixTs = response.payload[0]?.unixTs;
    return unixTs !== undefined ? dayjs.unix(unixTs) : undefined;
  }, []);

  const key = useMemo(
    () => (shouldFetch ? [`/v2/websites/${websiteId}/raw/visits`, payload] : null),
    [payload, shouldFetch, websiteId],
  );

  return useFetchData<Dayjs | undefined>(key, config, mapper, fetchFn);
};
